import { IconCopy, IconShare } from '@hulu-react-style-components/icons';
import envConfig from '@hulu/env-config';
import React from 'react';

import type { Asset2TranscodingStatus, Maybe } from '../../../../../apis/graphql';
import { Asset2ReviewStatus } from '../../../../../apis/graphql';
import { stringifyApprovedPublishers } from '../../../../../configs';
import type { Asset2 } from '../../../../../models';
import { isExtendedVideoAsset } from '../../../../../utils/assets';
import { stringifyTagList } from '../../../../../utils/formatting';
import { getColor } from '../../../../AssetPreview/util';
import Pill from '../../../../Pill';
import { getTranscodeColor } from '../../../../Pill/constants';
import AssetRejectionReasonsModal from '../../../../StatusModal/AssetRejectionReasonsModal/AssetRejectionReasonsModal';
import { bytesToLabel, msToTime } from '../../../util/formatters';
import { getAcceptableUrl } from '../../../util/getAcceptableUrl';
import { AssetDownloadButton } from '../../atoms/AssetDownloadButton';
import { VideoPlayer, VideoPlayerPlaceholder } from '../VideoPlayer/VideoPlayer';
import { StatusModalWithPill } from './atoms/StatusModalWithPill/StatusModalWithPill';
import {
  AssetDetailsText,
  Card,
  CopyButton,
  CopyIcon,
  FileData,
  FileDetails,
  FileDownload,
  FileName,
  IconAnchor,
  MetaDataTable,
  Preview,
  ShareIcon,
} from './styles';
import { RejectionReasonsActivator } from './styles/RejectionReasonsActivator';

export type Props = {
  asset: Asset2;
  creativeId?: string;
  derivedCreativeReviewStatus?: Asset2ReviewStatus;
  derivedCreativeTranscodingStatus?: Maybe<Asset2TranscodingStatus>;
  /** When false, video assets render a placeholder and the blob download is not started. Defaults to true. */
  loadPreview?: boolean;
};

const ASSET_ID_LABEL = 'Asset ID';
const LAST_QUALIFIED_LABEL = 'Last Qualified';
const CREATIVE_ID_LABEL = 'Creative ID';
const UPLOADED_LABEL = 'Uploaded';
const LAST_SEE_LABEL = 'Updated';
const ASSET_TAG_LABEL = 'Asset Tag';
const APPROVED_PUBLISHERS_LABEL = 'Approved Publishers';
const ASSET_REVIEW_STATUS_LABEL = 'Asset Review Status';
const ASSET_TRANSCODING_STATUS_LABEL = 'Asset Transcoding Status';
const DERIVED_CREATIVE_REVIEW_STATUS_LABEL = 'Derived Creative Review Status';
const DERIVED_CREATIVE_TRANSCODING_STATUS_LABEL = 'Derived Creative Transcoding Status';
const SEE_REJECTION_REASONS_LABEL = 'See Rejection Reason';

export const AssetPreviewCard: React.FC<Props> = ({
  asset,
  creativeId,
  derivedCreativeReviewStatus,
  derivedCreativeTranscodingStatus,
  loadPreview = true,
}) => {
  const { review, s3Url, filename, id, createdAt, updatedAt, lastSeenAt, assetTagList, approvedPublishers } = asset;

  const handleCopyAssetId = (): void => {
    navigator.clipboard.writeText(id);
  };

  const transcodeStatus = isExtendedVideoAsset(asset) ? asset.transcode?.status : null;

  const downloadUrl = `${envConfig.REACT_APP_CAMPAIGN_GRAPHQL_ORIGIN}/api/asset/${id}`;
  const downloadFilename = filename || '';
  const acceptableUrl = getAcceptableUrl(false, id);

  const previewComponent = s3Url ? (
    <div data-testid="preview-component">
      <Preview imageUrl={s3Url}>
        <StatusModalWithPill asset={asset} />
      </Preview>
    </div>
  ) : null;

  const isAssetRejected = review === Asset2ReviewStatus.Declined;

  return (
    <Card>
      {isExtendedVideoAsset(asset) ? (
        loadPreview ? (
          <VideoPlayer asset={asset}>
            <StatusModalWithPill asset={asset} />
          </VideoPlayer>
        ) : (
          <VideoPlayerPlaceholder>
            <StatusModalWithPill asset={asset} />
          </VideoPlayerPlaceholder>
        )
      ) : (
        previewComponent
      )}
      <FileData>
        <FileName>{isExtendedVideoAsset(asset) && asset.filename}</FileName>
        <FileDetails style={{ display: 'flex' }}>
          {isExtendedVideoAsset(asset) && (
            <>
              <span>{msToTime(asset.videoMetadata?.duration)}</span>
              <span>{bytesToLabel(asset.videoMetadata?.fileSizeBytes)}</span>
            </>
          )}
          <FileDownload>
            <AssetDownloadButton filename={downloadFilename} url={downloadUrl} />
          </FileDownload>
        </FileDetails>
      </FileData>
      <MetaDataTable>
        <tbody>
          <tr>
            <th>{ASSET_ID_LABEL}</th>
            <td>
              {id}
              <CopyButton type="button" onClick={handleCopyAssetId} aria-label="Copy Asset ID" title="Copy Asset ID">
                <CopyIcon as={IconCopy} />
              </CopyButton>
              {acceptableUrl && (
                <IconAnchor href={acceptableUrl} target="_blank">
                  <AssetDetailsText>Asset Details</AssetDetailsText> <ShareIcon as={IconShare} />
                </IconAnchor>
              )}
            </td>
          </tr>
          <tr>
            <th>{LAST_QUALIFIED_LABEL}</th>
            <td>{lastSeenAt ?? '—'}</td>
          </tr>
          {creativeId && (
            <tr>
              <th>{CREATIVE_ID_LABEL}</th>
              <td>{creativeId}</td>
            </tr>
          )}
          {review && (
            <tr>
              <th>{ASSET_REVIEW_STATUS_LABEL}</th>
              <td>
                <Pill text={review} color={getColor(review)} />
              </td>
            </tr>
          )}
          {transcodeStatus && (
            <tr>
              <th>{ASSET_TRANSCODING_STATUS_LABEL}</th>
              <td>
                <Pill text={transcodeStatus} color={getTranscodeColor(transcodeStatus)} />
              </td>
            </tr>
          )}
          {derivedCreativeReviewStatus && (
            <tr>
              <th>{DERIVED_CREATIVE_REVIEW_STATUS_LABEL}</th>
              <td>
                <Pill text={derivedCreativeReviewStatus} color={getColor(derivedCreativeReviewStatus)} />
              </td>
            </tr>
          )}
          {derivedCreativeTranscodingStatus && (
            <tr>
              <th>{DERIVED_CREATIVE_TRANSCODING_STATUS_LABEL}</th>
              <td>
                <Pill
                  text={derivedCreativeTranscodingStatus}
                  color={getTranscodeColor(derivedCreativeTranscodingStatus)}
                />
              </td>
            </tr>
          )}
          <tr>
            <th>{UPLOADED_LABEL}</th>
            <td>{createdAt}</td>
          </tr>
          <tr>
            <th>{LAST_SEE_LABEL}</th>
            <td>{updatedAt}</td>
          </tr>
          <tr>
            <th>{ASSET_TAG_LABEL}</th>
            <td>{stringifyTagList(assetTagList ?? [])}</td>
          </tr>
          <tr>
            <th>{APPROVED_PUBLISHERS_LABEL}</th>
            <td>{stringifyApprovedPublishers(approvedPublishers)}</td>
          </tr>
        </tbody>
      </MetaDataTable>
      {isAssetRejected && asset.rejectionReasons && (
        <AssetRejectionReasonsModal
          asset={asset}
          customActivator={
            <RejectionReasonsActivator data-testid="rejection-reason-button">
              {`${SEE_REJECTION_REASONS_LABEL}${asset.rejectionReasons.length > 1 ? 's' : ''}`}
            </RejectionReasonsActivator>
          }
        />
      )}
    </Card>
  );
};
